import Modal from "@/components/base/Modal";
import Button from "@/components/base/Button";
import Badge from "@/components/base/Badge";
import { EmptyState } from "@/components/base/StateViews";
import { formatDate, formatMoney, plural } from "@/utils/format";
import { usePager } from "@/hooks/usePager";
import Pagination from "@/components/base/Pagination";
import { buildVersementReceipt, type FeeSnapshot, type ReceiptData, type ReceiptStudent } from "@/utils/receipt";
import { PAYMENT_METHOD_LABEL } from "@/services/paymentService";
import type { Receipt } from "@/types";

interface Props {
  open: boolean;
  onClose: () => void;
  student: ReceiptStudent;
  fees: FeeSnapshot[];
  receipts: Receipt[];
  onEncaisser: () => void;
  onPrintReceipt: (data: ReceiptData) => void;
  /** Absent : l'utilisateur ne peut pas annuler de reçu. */
  onCancelReceipt?: (receipt: Receipt) => void;
}

export default function StudentPaymentsModal({
  open,
  onClose,
  student,
  fees,
  receipts,
  onEncaisser,
  onPrintReceipt,
  onCancelReceipt,
}: Props) {
  const totalDue = fees.reduce((s, f) => s + f.due, 0);
  const totalPaid = fees.reduce((s, f) => s + f.paid, 0);
  const remaining = Math.max(0, totalDue - totalPaid);
  const sortedVersements = [...receipts].sort((a, b) => b.date.localeCompare(a.date) || b.receiptNo.localeCompare(a.receiptNo));
  const receiptsPage = usePager().slice(sortedVersements, student.matricule);

  const printReceipt = (r: Receipt) => {
    onPrintReceipt(buildVersementReceipt({ receipt: r, fees, student }));
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Suivi des paiements"
      subtitle={`${student.name} • ${student.className} • ${student.schoolYear}`}
      icon="ri-history-line"
      size="xl"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Fermer
          </Button>
          <Button icon="ri-hand-coin-line" onClick={onEncaisser}>
            Encaisser un versement
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="rounded-md border border-background-200 bg-background-100/50 p-3.5">
            <p className="text-xs uppercase tracking-wide text-foreground-400">Total dû</p>
            <p className="mt-1 text-lg font-semibold font-heading text-foreground-950">{formatMoney(totalDue)}</p>
          </div>
          <div className="rounded-md border border-background-200 bg-background-100/50 p-3.5">
            <p className="text-xs uppercase tracking-wide text-foreground-400">Total versé</p>
            <p className="mt-1 text-lg font-semibold font-heading text-primary-700">{formatMoney(totalPaid)}</p>
          </div>
          <div className="rounded-md border border-background-200 bg-background-100/50 p-3.5">
            <p className="text-xs uppercase tracking-wide text-foreground-400">Reste à payer</p>
            <p className="mt-1 text-lg font-semibold font-heading text-accent-700">{formatMoney(remaining)}</p>
          </div>
        </div>

        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-foreground-400">
            Situation des frais ({student.schoolYear})
          </p>
          <div className="overflow-hidden rounded-md border border-background-200">
            <table className="w-full text-sm">
              <thead className="bg-background-100">
                <tr className="text-left text-xs uppercase tracking-wide text-foreground-500">
                  <th className="px-3.5 py-2 font-medium">Frais</th>
                  <th className="px-3.5 py-2 text-right font-medium">Dû</th>
                  <th className="px-3.5 py-2 text-right font-medium">Payé</th>
                  <th className="px-3.5 py-2 text-right font-medium">Reste</th>
                </tr>
              </thead>
              <tbody>
                {fees.map((f) => {
                  const rest = Math.max(0, f.due - f.paid);
                  return (
                    <tr key={f.key} className="border-t border-background-200">
                      <td className="px-3.5 py-2.5 text-foreground-800">{f.label}</td>
                      <td className="px-3.5 py-2.5 text-right tabular-nums text-foreground-700">{formatMoney(f.due)}</td>
                      <td className="px-3.5 py-2.5 text-right tabular-nums text-primary-700">{formatMoney(f.paid)}</td>
                      <td className="px-3.5 py-2.5 text-right tabular-nums">
                        {rest > 0 ? (
                          <span className="text-accent-700">{formatMoney(rest)}</span>
                        ) : (
                          <Badge tone="success">Soldé</Badge>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {fees.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-3.5 py-4 text-center text-xs text-foreground-400">
                      Aucun frais enregistré pour cette année.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-foreground-400">
              Historique des versements
            </p>
            <Badge tone="secondary" icon="ri-receipt-line">
              {plural(sortedVersements.filter((r) => !r.cancelledAt).length, "reçu")}
            </Badge>
          </div>
          {sortedVersements.length === 0 ? (
            <EmptyState
              icon="ri-receipt-line"
              title="Aucun versement enregistré"
              message="Les encaissements effectués pour cette année apparaîtront ici avec leur reçu."
            />
          ) : (
            <>
            <div className="overflow-hidden rounded-md border border-background-200">
              <table className="w-full text-sm">
                <thead className="bg-background-100">
                  <tr className="text-left text-xs uppercase tracking-wide text-foreground-500">
                    <th className="px-3.5 py-2 font-medium">N° reçu</th>
                    <th className="px-3.5 py-2 font-medium">Date</th>
                    <th className="px-3.5 py-2 font-medium">Frais</th>
                    <th className="px-3.5 py-2 font-medium">Mode</th>
                    <th className="px-3.5 py-2 text-right font-medium">Montant</th>
                    <th className="px-3.5 py-2 text-right font-medium">Reçu</th>
                  </tr>
                </thead>
                <tbody>
                  {receiptsPage.items.map((v) => (
                    <tr
                      key={v.id}
                      className={`border-t border-background-200 hover:bg-background-100/60 ${v.cancelledAt ? "opacity-60" : ""}`}
                    >
                      <td className="px-3.5 py-2.5 font-medium text-foreground-900">
                        <span className={v.cancelledAt ? "line-through" : ""}>{v.receiptNo}</span>
                        {v.cancelledAt && (
                          <p className="text-xs font-normal text-accent-700" title={v.cancelReason}>
                            Annulé — {v.cancelReason}
                          </p>
                        )}
                      </td>
                      <td className="px-3.5 py-2.5 text-foreground-600">{formatDate(v.date)}</td>
                      <td className="px-3.5 py-2.5 text-foreground-800">{v.lines.map((l) => l.label).join(", ")}</td>
                      <td className="px-3.5 py-2.5 text-foreground-600">{PAYMENT_METHOD_LABEL[v.method]}</td>
                      <td className="px-3.5 py-2.5 text-right tabular-nums text-foreground-900">{formatMoney(v.total)}</td>
                      <td className="px-3.5 py-2.5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => printReceipt(v)}
                            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-2.5 py-1 text-xs text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                          >
                            <i className="ri-printer-line"></i>
                            Imprimer
                          </button>
                          {onCancelReceipt && !v.cancelledAt && (
                            <button
                              onClick={() => onCancelReceipt(v)}
                              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-1 text-xs text-foreground-500 hover:bg-accent-50 hover:text-accent-700 transition-colors cursor-pointer"
                              title="Annuler ce reçu"
                            >
                              <i className="ri-close-circle-line"></i>
                              Annuler
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination pager={receiptsPage} className="!px-0" />
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}